/**
 * One resolved evidence item, and where — if anywhere — its source record can be read.
 *
 * Pure: no React, no clock, no request.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  "SEEING THE INSIGHT" AND "SEEING THE ROW" ARE DIFFERENT PERMISSIONS
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * API_CONTRACT §8.9 item 6: an insight outlives the access that produced it, so every
 * reference is re-checked against the caller's scope **today**. An item the caller may
 * no longer read stays in place with `accessible: false` and **no `value` key at all**
 * — "vắng mặt, chứ không phải `null` (một khoá mang `null` bị renderer đọc là 'đã đo,
 * và kết quả là không có gì')".
 *
 * So the inaccessible variant below carries **no `value` member**, matching the wire
 * exactly. A component cannot print an empty object, a dash or a zero for a refused
 * row, because there is nothing in the model to reach for.
 *
 * Fabricated, deleted and out-of-scope are **three causes with one result**, and the
 * caller is deliberately not told which — the same decision WP-M2 takes when it
 * validates evidence. This module therefore does not invent a reason it cannot know;
 * `no_value_published` is a *different* fact (the server said accessible and sent
 * nothing), and it is kept apart because it is a server defect rather than a refusal.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  THE PLAN ASKS FOR A LINK TO "ĐÚNG BẢN GHI NGUỒN". TWO OF THREE KINDS HAVE NONE.
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * `ref` is a row id. Checked against `openapi.yaml`:
 *
 *   `productivity_score`  `GET /api/v1/productivity/scores/{id}` **exists**.
 *   `daily_aggregate`     nothing addresses `wp_daily_aggregates.id`.
 *                         `GET /activities/timeline` is addressed by date window.
 *   `alert`               there is no `GET /api/v1/alerts/{id}` — the collection and
 *                         `POST /alerts/{id}/acknowledge`, and nothing else.
 *
 * A dead link on an investigation record reads as missing evidence (Task 53 §8), so
 * where there is no read path the reference is **printed and no link is rendered**,
 * with the endpoint that would close it named on screen. Nothing is mocked.
 *
 * The other three `kind` values `AiEvidenceRef` publishes — `hourly_aggregate`,
 * `attendance_day`, `time_entry` — are **never emitted by this build's generator**
 * (API_CONTRACT §8.9, Task 55 item 2). They are still rendered if one ever appears,
 * because a client that dropped an unknown kind would hide the day somebody adds one.
 */

/** Every `kind` the wire may carry, from `AiEvidenceRef`. */
export const EVIDENCE_KINDS = [
  'daily_aggregate',
  'hourly_aggregate',
  'attendance_day',
  'productivity_score',
  'alert',
  'time_entry',
] as const;

export type EvidenceKind = (typeof EVIDENCE_KINDS)[number];

/** The three `retrieval.ts` actually mints. The other three are documented absences. */
export const EMITTED_EVIDENCE_KINDS: readonly EvidenceKind[] = [
  'daily_aggregate',
  'productivity_score',
  'alert',
];

export type EvidenceReading =
  | {
      readonly kind: 'accessible';
      readonly evidenceId: string;
      readonly refKind: string;
      readonly ref: string;
      readonly value: Readonly<Record<string, unknown>>;
      /**
       * `value.freshness` when the item is a score — G16 put the three §8.6 fields on
       * this endpoint too. Typed `unknown` so `readFreshness` stays the only reader,
       * for the fail-closed reason that module gives.
       */
      readonly freshness: unknown;
    }
  /** **No `value` member**, on either arm. See the header. */
  | {
      readonly kind: 'withheld';
      readonly evidenceId: string;
      readonly refKind: string;
      readonly ref: string;
      readonly reason: 'not_accessible' | 'no_value_published';
    }
  | { readonly kind: 'malformed' };

/**
 * One item of `AiEvidenceResponse.evidence[]`.
 *
 * An `accessible: true` carrying no `value` is **not** read as an ordinary refusal:
 * the server said the caller may see the row and then sent nothing, which is a defect
 * worth naming rather than folding into the refusal count an operator reads.
 */
export function readEvidenceItem(item: unknown): EvidenceReading {
  if (typeof item !== 'object' || item === null) return { kind: 'malformed' };
  const record = item as Record<string, unknown>;
  const evidenceId = record.evidence_id;
  const refKind = record.kind;
  const ref = record.ref;
  const accessible = record.accessible;
  if (
    typeof evidenceId !== 'string' ||
    typeof refKind !== 'string' ||
    typeof ref !== 'string' ||
    typeof accessible !== 'boolean'
  ) {
    return { kind: 'malformed' };
  }
  if (!accessible) {
    return { kind: 'withheld', evidenceId, refKind, ref, reason: 'not_accessible' };
  }
  const value = record.value;
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return { kind: 'withheld', evidenceId, refKind, ref, reason: 'no_value_published' };
  }
  const bag = value as Record<string, unknown>;
  return {
    kind: 'accessible',
    evidenceId,
    refKind,
    ref,
    value: bag,
    freshness: bag.freshness,
  };
}

/**
 * Where the underlying row can be read, if anywhere.
 *
 * `reachable` names an **API path**, not a screen route, and the distinction is
 * stated rather than blurred: this build has no screen addressed by
 * `wp_productivity_scores.id`, so what a reader gets is the request they can run —
 * the technique `overview/PendingSlots.tsx` used for an unbuilt screen. Presenting it
 * as a UI link would promise a page that does not exist.
 */
export type EvidenceTarget =
  | { readonly kind: 'reachable'; readonly request: string }
  | { readonly kind: 'noEndpoint'; readonly needed: string }
  | { readonly kind: 'notEmitted'; readonly needed: string };

const PRODUCTIVITY_SCORE_BY_ID = '/api/v1/productivity/scores';

export function evidenceTarget(refKind: string, ref: string): EvidenceTarget {
  switch (refKind) {
    case 'productivity_score':
      return {
        kind: 'reachable',
        request: `GET ${PRODUCTIVITY_SCORE_BY_ID}/${encodeURIComponent(ref)}`,
      };
    case 'daily_aggregate':
      return {
        kind: 'noEndpoint',
        needed: 'GET /api/v1/activities/timeline theo wp_daily_aggregates.id',
      };
    case 'alert':
      return { kind: 'noEndpoint', needed: 'GET /api/v1/alerts/{id}' };
    case 'hourly_aggregate':
    case 'attendance_day':
    case 'time_entry':
      return { kind: 'notEmitted', needed: `GET /api/v1/… theo id cho kind=${refKind}` };
    default:
      return { kind: 'notEmitted', needed: `kind=${refKind}` };
  }
}

/**
 * How many of a page of items the caller may actually read.
 *
 * Counted from the readings rather than from `accessible`, so a `no_value_published`
 * item is **not** counted as delivered — the number beside "n trong m" has to mean
 * "rows you were shown", which is also what `record_count` means in the
 * `wp_data_access_logs` row this read writes (SEC-35).
 */
export function accessibleCount(readings: readonly EvidenceReading[]): number {
  return readings.filter((reading) => reading.kind === 'accessible').length;
}
